import { inject, Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, exhaustMap, filter, map, mergeMap, switchMap } from 'rxjs/operators';

import { ConfigurationFacade } from '../facades/configuration.facade';
import { MarketingFacade } from '../facades/marketing.facade';
import {
  AppActions,
  CheckoutActions,
  CpqActions,
  CustomerActions,
  OrderApprove,
  ShopCartActions,
} from '../actions';
import { PayloadUtils } from '../utils/payload.utils';
import { CpqFacade } from '../facades/cpq.facade';
import { CARTS_API_ERROR_CODES_TO_SHOW_ALERT, EGlueResource } from '../configurations/common';
import { ICart, ICartPayload, ICartUpdateRequest } from '../models/cart.models';
import { I18nService } from '../services';
import { Position } from '../models/alert.models';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment.defaults';

@Injectable()
export class ShopCartEffects {
  private readonly actions$: Actions = inject(Actions);
  private readonly cpqFacade: CpqFacade = inject(CpqFacade);
  private readonly marketingFacade: MarketingFacade = inject(MarketingFacade);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private readonly i18nService: I18nService = inject(I18nService);
  private readonly router: Router = inject(Router);

  readonly initCurrentCartAfterLogin$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.CompanyUsersActionSuccess),
      filter(() => !this.router.url.includes('/ext')),
      map(() => ShopCartActions.loadCurrentCartAndCartItems()),
    );
  });

  readonly postNewCartWithItem$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.postNewCartWithItem),
      mergeMap(action =>
        this.marketingFacade.postCart()
          .pipe(
            exhaustMap((newCart) => [
              ShopCartActions.postCartSuccess({newCartData: newCart}),
              ShopCartActions.addItemToCart({
                cartId: newCart.data.id,
                requestBody: action.requestBody,
                product: action.product,
                redirectUrl: action.redirectUrl,
                retryWithNewCart: true,
              }),
            ]),
            catchError(error => of(ShopCartActions.postCartFail({error}))),
          ),
      ),
    );
  });

  /**
   * Creates a new cart with the copied attributes and adds the item without touching the store,
   * so the mini-cart does not flicker. The user then decides which cart stays active.
   * On error, the add to cart error is shown and the current cart is reloaded.
   * If the cart was created but adding the item fails, the current cart is set as default again
   * and the empty new cart is deleted first (also when setting the current cart as default fails).
   */
  postNewCartWithItemForSwitchDecision$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.postNewCartWithItemForSwitchDecision),
      mergeMap(action =>
        this.marketingFacade.postCart(action.cartAttributes).pipe(
          switchMap(newCart => this.marketingFacade.addItemToCart(newCart.data.id, action.requestBody).pipe(
            map(cartResponse => ShopCartActions.requestCartSwitchDecision({
              pending: {
                currentCartId: action.currentCartId,
                cartResponse,
                productSku: action.product.sku,
                productName: action.product.name,
              },
            })),
            catchError(error => {
              const errorActions: Action[] = [
                ShopCartActions.addItemToCartError({error}),
                ShopCartActions.loadCurrentCartAndCartItems(),
              ];
              // Restore and delete are independent, a failure of one does not skip the other or the error actions
              return this.restoreDefaultCart(action.currentCartId).pipe(
                catchError(() => of(null)),
                concatMap(() => this.marketingFacade.deleteCartById(newCart.data.id).pipe(
                  catchError(() => of(null)),
                )),
                concatMap(() => errorActions),
              );
            }),
          )),
          catchError(error => of(
            ShopCartActions.addItemToCartError({error}),
            ShopCartActions.loadCurrentCartAndCartItems(),
          )),
        ),
      ),
    );
  });

  /**
   * Opens the mini-cart together with the cart switch decision modal.
   * On the cart page, the mini-cart stays closed and only the modal is shown.
   */
  requestCartSwitchDecision$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.requestCartSwitchDecision),
      filter(() => !this.router.url.includes('shop-cart')),
      map(() => ShopCartActions.toggleMiniCart({isOpen: true})),
    );
  });

  /**
   * Puts the new cart with the added item into the store as the current cart
   * and shows the add to cart notification (outside the cart page).
   */
  switchToNewCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.switchToNewCart),
      concatMap(({pending}) => {
        const dispatchList: Action[] = [
          // The reload would race with the already added item and could overwrite the cart with an empty one
          ShopCartActions.postCartSuccess({newCartData: pending.cartResponse, skipCartReload: true}),
          ShopCartActions.loadCartItemsSuccess({cartItems: pending.cartResponse}),
          ShopCartActions.addLastAddedItemName({itemName: pending.productName}),
        ];
        if (!this.router.url.includes('shop-cart')) {
          dispatchList.push(ShopCartActions.openMiniCartAndShowNotificationAfterAddToCart());
        }
        return dispatchList;
      }),
    );
  });

  /**
   * Sets the current cart as default again on the backend (the new cart became default on its creation).
   * On error, the current cart is reloaded.
   */
  stayOnCurrentCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.stayOnCurrentCart),
      // Only the backend default has to be restored, the store still holds the previous cart
      concatMap(({pending}) => this.restoreDefaultCart(pending.currentCartId).pipe(
        map(currentCart => ShopCartActions.stayOnCurrentCartSuccess({currentCart})),
        catchError(() => of(ShopCartActions.loadCurrentCartAndCartItems())),
      )),
    );
  });

  readonly checkIsCartDefault$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.checkIsCartDefault),
      concatMap((action) =>
        this.marketingFacade.selectCartById(action.cartId)
          .pipe(concatMap((cart) => {
              if (cart?.attributes?.isDefault) {
                return of(ShopCartActions.addItemToCart({
                  cartId: action.cartId,
                  requestBody: action.requestBody,
                  product: action.product,
                  redirectUrl: action.redirectUrl,
                  retryWithNewCart: true,
                }));
              } else {
                return of(ShopCartActions.addItemToCartLoadCarts({
                  cartId: action.cartId,
                  requestBody: action.requestBody,
                  product: action.product,
                  redirectUrl: action.redirectUrl,
                }));
              }
            }),
            catchError(() => of(ShopCartActions.addItemToCartLoadCarts({
              cartId: action.cartId,
              requestBody: action.requestBody,
              product: action.product,
              redirectUrl: action.redirectUrl,
            }))),
          )),
    );
  });

  readonly addItemToCartLoadCarts$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.addItemToCartLoadCarts),
      exhaustMap((action) =>
        this.marketingFacade.getCarts()
          .pipe(
            mergeMap((carts) => [
              ShopCartActions.loadCartsSuccess({carts}),
              ShopCartActions.addItemToCartLoadCartsSuccess({
                cart: carts.data.find((cart: ICart) => cart.attributes.isDefault),
                product: action.product,
                requestBody: action.requestBody,
                redirectUrl: action.redirectUrl,
              }),
            ]),
            catchError(error => of(ShopCartActions.loadCartsFail({error}))),
          )),
    );
  });

  readonly addItemToCartLoadCartsSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.addItemToCartLoadCartsSuccess),
      exhaustMap(action =>
        this.marketingFacade.getCartItems(action.cart.id)
          .pipe(
            mergeMap((cartItems) => [
              ShopCartActions.loadCartSuccess({cart: action.cart}),
              ShopCartActions.loadCartItemsSuccess({cartItems}),
              ShopCartActions.loadCurrentCartItemsSuccess(),
              ShopCartActions.addItemToCart({
                cartId: action.cart.id,
                requestBody: action.requestBody,
                product: action.product,
                redirectUrl: action.redirectUrl,
                retryWithNewCart: true,
              }),
            ]),
            catchError(error => of(ShopCartActions.addItemFailAfterCartLoad({error}))),
          )),
    );
  });

  readonly addItemToCart$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.addItemToCart),
      concatMap(action =>
        this.marketingFacade.addItemToCart(action.cartId, action.requestBody)
          .pipe(
            concatMap((cartResponse) => {
              const dispatchList: Action[] = [
                ShopCartActions.addItemToCartSuccess(
                  {cartResponse, itemData: action.requestBody, product: action.product}),
                ShopCartActions.loadCartItemsSuccess({cartItems: cartResponse}),
              ];
              if (action.product?.type === 'contract') {
                const itemsWithDetails: any[] = PayloadUtils.getItemsWithDetailsFromInclude(cartResponse.included);
                dispatchList.push(
                  CpqActions.setSelectedSystem({
                    selectedSystem: itemsWithDetails.find(item => item.attributes.abstractSku === action.product.sku),
                  }),
                  CpqActions.updateChangeLog({changeLog: []}),
                  AppActions.redirectToUrl({url: action.redirectUrl, replaceUrl: false}),
                );
              } else if (!this.router.url.includes('shop-cart')) {
                dispatchList.push(ShopCartActions.openMiniCartAndShowNotificationAfterAddToCart());
              }
              return dispatchList;
            }),
            catchError(error => {
              if (!action.retryWithNewCart) {
                return of(ShopCartActions.addItemToCartError({error}));
              }
              return of(ShopCartActions.handleAddToCartFail({
                error,
                cartId: action.cartId,
                requestBody: action.requestBody,
                product: action.product,
                redirectUrl: action.redirectUrl,
              }));
            }),
          ),
      ));
  });

  readonly deleteItemsFromCart$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.deleteItemsFromCart),
      mergeMap(action =>
        this.marketingFacade.deleteItemsFromCart(action.cartId)
          .pipe(
            mergeMap((cartResponse) => {
              const payload: ICartUpdateRequest = {
                data: {
                  type: EGlueResource.CARTS,
                  attributes: {
                    name: cartResponse.data.attributes.name,
                    ...action.updateAttributes,
                  },
                },
              };

              return [
                ShopCartActions.deleteItemsFromCartSuccess({cartResponse: cartResponse}),
                ShopCartActions.startCartOperation(),
                ShopCartActions.updateCartData({
                  cartId: action.cartId,
                  cartData: payload,
                  eTag: cartResponse.eTag ?? '',
                }),
              ];
            }),
            catchError(error => {
              return of(ShopCartActions.deleteItemsFromCartError({error}));
            }),
          ),
      ));
  });

  readonly addItemToCartError$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.addItemToCartError),
      mergeMap(() =>
        this.configurationFacade.getTranslationByKey('error.add-item-fail').pipe(
          map(message =>
            AppActions.setAlert({
              alert: {
                type: 'error',
                message,
              },
            }),
          ),
        ),
      ),
    );
  });

  readonly loadCartsAfterPostCartSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.postCartSuccess),
      filter(action => !action.skipCartReload),
      map(() =>
        ShopCartActions.loadCurrentCartAndCartItems(),
      ),
    );
  });

  readonly addItemsToCartFromCsvFile$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.addToCartFromCsvFile),
      mergeMap((action) =>
        this.marketingFacade.addItemsToCartFromCsvFile(action.cartId, action.requestBody).pipe(
          mergeMap(cart => [
            ShopCartActions.addToCartFromCsvFileSuccess({cart}),
            ShopCartActions.updateShopCartAfterItemUpdate({payload: cart}),
          ]),
          catchError(error => of(ShopCartActions.addToCartFromCsvFileFail({error}))),
        ),
      ),
    );
  });

  readonly handleAddToCartFail$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.handleAddToCartFail),
      switchMap((action) => {
        const errorCodes: string[] = action?.error?.map((error: any) => error.code);
        const invalidCodes: string[] = CARTS_API_ERROR_CODES_TO_SHOW_ALERT;
        if (errorCodes.some((code: string) => invalidCodes.includes(code))) {
          const errorObj: any = action.error.find((error: any) => invalidCodes.includes(error.code));
          return of(ShopCartActions.handleAddToCartResetState({
            error: errorObj,
            messageKey: errorObj.code !== '102' || errorObj.detail.includes('cart-cannot-be-mixed')
              ? errorObj.detail : 'error.product-unavailable',
            params: {name: action.product.name, subsequentMaterial: action.product.subsequentMaterial},
          }));
        }
        return of(ShopCartActions.createCartThanAddItem({
          error: action.error,
          cartId: action.cartId,
          requestBody: action.requestBody,
          product: action.product,
          redirectUrl: action.redirectUrl,
        }));
      }),
    );
  });

  readonly handleAddToCartResetState$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.handleAddToCartResetState),
      mergeMap((action) => {
          if (action?.error?.detail === 'product-cart.validation.error.item-not-available') {
            return showDiscontinuedProductWarning(this.i18nService.getCurrentLocale(), action.params);
          } else {
            return this.configurationFacade.getTranslationByKey(action.messageKey ?? '', action.params).pipe(
              map(message =>
                AppActions.setAlert({
                  alert: {
                    type: 'error',
                    message,
                  },
                }),
              ),
            );
          }
        },
      ),
    );
  });

  readonly createCartThanAddItem$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.createCartThanAddItem),
      switchMap(action => {
        return this.marketingFacade.postCart()
          .pipe(
            exhaustMap((newCart) => [
              ShopCartActions.postCartSuccess({newCartData: newCart}),
              ShopCartActions.addItemToCart({
                cartId: newCart.data.id,
                requestBody: action.requestBody,
                product: action.product,
                redirectUrl: action.redirectUrl,
                retryWithNewCart: false,
              }),
            ]),
            catchError(error => of(ShopCartActions.postCartFail({error}))),
          );
      }),
    );
  });

  readonly loadCartItems$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.loadCartItems),
      exhaustMap(action =>
        this.marketingFacade.getCartItems(action.cartId)
          .pipe(
            map(cartItems => ShopCartActions.loadCartItemsSuccess({cartItems})),
            catchError(error => of(ShopCartActions.loadCartItemsFail({error}))),
          )),
    );
  });

  readonly loadCarts$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.loadCarts),
      mergeMap(() =>
        this.marketingFacade.getCarts()
          .pipe(
            map(carts => ShopCartActions.loadCartsSuccess({carts})),
            catchError(error => of(ShopCartActions.loadCartsFail({error}))),
          )),
    );
  });

  readonly setDefaultCartItemsInfo$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.loadCartItemsSuccess),
      map((action) =>
        ShopCartActions.setDefaultCartItems({cartItems: action.cartItems}),
      ),
    );
  });

  readonly updateCartDataEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.updateCartData),
      mergeMap(action =>
        this.marketingFacade.updateCartData(action.cartId, action.cartData, action.eTag).pipe(
          mergeMap((cart: ICartPayload) => [
            ShopCartActions.updateCartSystemDetailsSuccess({cart: cart}),
          ]),
          catchError(error => of(ShopCartActions.updateCartDataFailed({error}))),
        ),
      ),
    );
  });

  readonly updateCartDataSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.updateCartDataSuccess),
      concatMap(() => [
        ShopCartActions.loadCurrentCartAndCartItems(),
      ]),
    );
  });

  readonly updateCartDataFailed$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.updateCartDataFailed),
      concatMap(error => [
        ShopCartActions.loadCartItemsFail({error: error.error}),
        ShopCartActions.stopCartOperation(),
      ]),
    );
  });

  readonly deleteCartSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.deleteCartSuccess),
      concatMap((action) => {
        if (action.isDefault) {
          // if the default cart was deleted, a new default is selected on the BE,
          // meaning that a refresh is necessary
          return [
            ShopCartActions.loadCurrentCartAndCartItems(),
          ];
        } else {
          return [];
        }
      }),
    );
  });

  readonly loadCurrentCartItemsSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.loadCurrentCartItemsSuccess),
      map(() => ShopCartActions.stopCartOperation()),
    );
  });

  readonly deleteCart$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.deleteCart, ShopCartActions.deleteCpqCartSuccess),
      exhaustMap(action => this.marketingFacade.selectCartById(action.cartId).pipe(
        exhaustMap(cart => this.marketingFacade.deleteCartById(action.cartId).pipe(
          concatMap(() => {
            // in case the cart isn't found among the users other carts, it's best to re-trigger loading of all carts
            // to refresh the data in the app state (which always happens for the default cart)
            const isDefault: boolean = cart ? cart.attributes.isDefault : true;
            const dispatchList: Action[] = [];
            dispatchList.push(ShopCartActions.deleteCartSuccess({cartId: action.cartId, isDefault}));
            if (action.redirectUrl !== '') {
              dispatchList.push(AppActions.redirectToUrl({url: action.redirectUrl, replaceUrl: true}));
            }
            return dispatchList;
          }),
        )),
        catchError(error => of(ShopCartActions.deleteCartFail({error}))),
      )),
    );
  });

  readonly loadCurrentCartAndCartItems$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.loadCurrentCartAndCartItems),
      concatMap(() =>
        this.marketingFacade.getCarts()
          .pipe(
            mergeMap((carts) => [
              ShopCartActions.startCartOperation(),
              ShopCartActions.loadCartsSuccess({carts}),
              ShopCartActions.loadCurrentCartItems({cart: carts.data.find((cart: ICart) => cart.attributes.isDefault)}),
            ]),
            catchError(error => of(ShopCartActions.loadCartsFail({error}))),
          )),
    );
  });

  readonly loadCurrentCart$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.loadCurrentCart),
      concatMap((id) =>
        this.marketingFacade.getCartItems(id.id)
          .pipe(
            mergeMap((cart) => [
              ShopCartActions.updateItemInCartLoadCartItemsSuccess({updatedItemInCartAndLoadedCartItems: cart}),
              ShopCartActions.updateShopCartAfterItemUpdate({payload: cart}),
            ]),
            catchError(error => of(ShopCartActions.loadCartsFail({error}))),
          )),
    );
  });

  readonly loadCurrentCartItems$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.loadCurrentCartItems),
      exhaustMap(action => {
        if (action.cart?.id) {
          return this.marketingFacade.getCartItems(action.cart.id)
            .pipe(
              mergeMap((cartItems) => {
                const actions: Action[] = [
                  ShopCartActions.loadCartSuccess({cart: action.cart}),
                  ShopCartActions.loadCartItemsSuccess({cartItems: {...cartItems}}),
                  ShopCartActions.loadCurrentCartItemsSuccess(),
                ];

                // Check if Saturday delivery is selected based on cart expenses
                if (action.cart?.attributes?.expenses?.some((expense: any) => expense.name === environment.sparePartSaturdayDeliveryKey)) {
                  actions.push(CheckoutActions.setIsSaturdayShipmentSelected({isSaturdayShipmentSelected: true}));
                }

                return actions;
              }),
              catchError(error => of(ShopCartActions.loadCartItemsFail({error}))),
            );
        } else {
          return this.marketingFacade.postCart()
            .pipe(
              mergeMap((newCartData) => [
                ShopCartActions.postCartSuccess({newCartData}),
                ShopCartActions.loadCurrentCartItemsSuccess(),
              ]),
              catchError(error => of(ShopCartActions.postCartFail({error}))),
            );
        }
      }));
  });

  readonly createEmptyCart$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.createEmptyCart),
      exhaustMap(action =>
        this.marketingFacade.postCart(action.attributes)
          .pipe(
            map((newCartData) => ShopCartActions.postCartSuccess({newCartData})),
            catchError(error => of(ShopCartActions.postCartFail({error}))),
          )),
    );
  });

  readonly clearCart$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.clearCart),
      exhaustMap(() =>
        this.marketingFacade.postCart()
          .pipe(
            map((newCartData) => ShopCartActions.postCartSuccess({newCartData})),
            catchError(error => of(ShopCartActions.postCartFail({error}))),
          )),
    );
  });

  readonly deleteItemFromCart$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.deleteItemFromCart),
      exhaustMap(action =>
        this.marketingFacade.deleteItemFromCart(action.cartId, action.itemId)
          .pipe(
            mergeMap(() => [
              ShopCartActions.addItemToCartInProgress({status: true}),
              ShopCartActions.deleteItemFromCartSuccess(),
              ShopCartActions.loadCurrentCart({id: action.cartId}),
            ]),
            catchError(error => of(ShopCartActions.deleteItemFromCartFail({error}))),
          )),
    );
  });

  readonly deleteItemFromCartOrderApprove$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApprove.deleteItemFromCart),
      mergeMap(action =>
        this.marketingFacade.deleteItemFromCart(action.cartId, action.itemId)
          .pipe(
            map(latestDeletedItemFromCart => OrderApprove.deleteItemFromCartSuccess({latestDeletedItemFromCart})),
            map(() => OrderApprove.loadCartItems({cartId: action.cartId})),
            catchError(error => of(OrderApprove.deleteItemFromCartFail({error}))),
          )),
    );
  });

  readonly updateItemInCartLoadCartItems$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.updateItemInCartLoadCartItems),
      switchMap(action =>
        this.marketingFacade.sequentialRequestPatchCartItemGetCartItems(action.cartId, action.itemId, action.updateItemInCartData)
          .pipe(
            mergeMap((updatedItemInCart: ICartPayload) => [
              ShopCartActions.updateShopCartAfterItemUpdate({payload: updatedItemInCart}),
            ]),
            catchError(error => of(ShopCartActions.updateItemInCartLoadCartItemsFail({error}))),
          )),
    );
  });

  readonly switchDefaultCart$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.switchDefaultCart),
      concatMap(action => this.marketingFacade.getCartItems(action.cartId).pipe(
        map(cartItems => ({cartItems, action})),
      )),
      concatMap(({cartItems, action}) => this.marketingFacade.updateCartData(
        cartItems.data.id,
        {data: {type: EGlueResource.CARTS, attributes: {...action.attributes, isDefault: true}}},
        cartItems.eTag ?? '',
      ).pipe(
        concatMap(cart => [
          ShopCartActions.loadCartItemsSuccess({cartItems: {...cart, included: cartItems.included}}),
          ShopCartActions.switchDefaultCartSuccess({cart}),
          ShopCartActions.loadCarts(),
        ]),
      )),
      catchError(error => of(ShopCartActions.switchDefaultCartFail({error}))),
    );
  });


  readonly deleteCpqQuote$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ShopCartActions.deleteCpqCart, ShopCartActions.quoteSummaryPageDeleteQuote),
      exhaustMap(action => this.cpqFacade.deleteCpqQuote(action.cartId).pipe(
        mergeMap(() => [
          ShopCartActions.deleteCpqCartSuccess({cartId: action.cartId, redirectUrl: action.redirectUrl}),
        ]),
        catchError(error => {
          if (error.error.errors.some((err: any) => err.code === '404')) {
            return of(ShopCartActions.deleteCpqCartSuccess({cartId: action.cartId, redirectUrl: action.redirectUrl}));
          } else {
            return of(ShopCartActions.deleteCpqCartFail({error}));
          }
        }),
      )),
    );
  });

  /**
   * Sets the cart as default on the backend. Its eTag is loaded first, as it is required for the update.
   * @param cartId - ID of the cart to set as default
   * @returns {Observable<ICartPayload>} updated cart
   */
  private restoreDefaultCart(cartId: string): Observable<ICartPayload> {
    return this.marketingFacade.getCartItems(cartId).pipe(
      concatMap(cartItems => this.marketingFacade.updateCartData(
        cartId,
        {data: {type: EGlueResource.CARTS, attributes: {isDefault: true}}},
        cartItems.eTag!,
      )),
    );
  }
}

function showDiscontinuedProductWarning(currentLocale: string, params: any): Observable<Action> {
  return of(AppActions.setAlert({
    alert: {
      type: 'warning',
      options: {
        alertWidth: '28rem',
        title: 'alert.discontinued-part-title',
        buttonLabel: 'alert.close',
        buttonPosition: Position.RIGHT,
      },
      message: params?.subsequentMaterial && params.subsequentMaterial.length != 0
        ? `alert.discontinued-part-message-with-subsequent-material`
        : 'alert.discontinued-part-message',
      messageData: params?.subsequentMaterial && params.subsequentMaterial.length != 0
        ? getSubsequentMaterial(currentLocale, params.subsequentMaterial)
        : undefined,
    },
  }));
}

function getSubsequentMaterial(currentLocale: string, subsequentMaterial: string): string {
  return `<a href="/${currentLocale}/product/A_${subsequentMaterial}">${subsequentMaterial}</a>`;
}
